import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import {
  Phone, Clock, AlertTriangle, PhoneCall, PhoneMissed,
  Users, CalendarClock, RefreshCw, ChevronDown, Loader2,
  MessageSquare, StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import LeadDetailDrawer from '@/components/training/LeadDetailDrawer';

const REASON_LABELS = {
  no_reply: 'No reply to AI',
  stopped_replying: 'Stopped replying',
  callback_requested: 'Requested callback',
  human_requested: 'Asked for a person',
  escalation: 'AI escalated',
  not_interested: 'May not be interested',
};

const CALLBACK_QUICK_OPTIONS = [
  { label: 'Tomorrow 10am', days: 1, hour: 10 },
  { label: 'Tomorrow 2pm', days: 1, hour: 14 },
  { label: 'In 3 days', days: 3, hour: 10 },
  { label: 'Next week', days: 7, hour: 10 },
];

const TrainingManualQueuePage = () => {
  const { selectedBusiness } = useAuth();
  const [activeTab, setActiveTab] = useState('queue');
  const [leads, setLeads] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inlineNotes, setInlineNotes] = useState({});

  const fetchQueue = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [queueRes, callbacksRes] = await Promise.all([
        fetch(`${API_URL}/api/leads?manual_followup_required=true&limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        }),
        fetch(`${API_URL}/api/callbacks?status=pending&limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        }),
      ]);
      if (queueRes.ok) {
        const data = await queueRes.json();
        setLeads(data.leads || data || []);
      }
      if (callbacksRes.ok) {
        const data = await callbacksRes.json();
        setCallbacks(data.callbacks || data || []);
      }
    } catch (err) {
      console.error('Queue fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue, selectedBusiness?.business_id]);

  const openDrawer = (leadId) => {
    setSelectedLeadId(leadId);
    setDrawerOpen(true);
  };

  const handleScheduleCallback = async (leadId, option) => {
    try {
      const token = localStorage.getItem('token');
      const callbackDate = new Date();
      callbackDate.setDate(callbackDate.getDate() + option.days);
      callbackDate.setHours(option.hour, 0, 0, 0);

      const res = await fetch(`${API_URL}/api/callbacks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          lead_id: leadId,
          scheduled_for: callbackDate.toISOString(),
          reason: 'Follow-up call',
          priority: 'medium',
        }),
      });
      if (res.ok) {
        toast.success(`Callback scheduled for ${option.label}`);
        fetchQueue();
      }
    } catch (err) {
      toast.error('Failed to schedule callback');
    }
  };

  const handleSaveInlineNote = async (leadId) => {
    const noteText = inlineNotes[leadId];
    if (!noteText?.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/leads/${leadId}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ content: noteText.trim() }),
      });
      if (res.ok) {
        toast.success('Note saved');
        setInlineNotes((prev) => ({ ...prev, [leadId]: '' }));
      }
    } catch (err) {
      toast.error('Failed to save note');
    }
  };

  // Stats
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];

  const todaysCallbacks = callbacks.filter((cb) => {
    const cbDate = cb.scheduled_for || cb.callback_date;
    return cbDate && cbDate.startsWith(todayStr);
  });

  const overdueCallbacks = callbacks.filter((cb) => {
    const cbDate = cb.scheduled_for || cb.callback_date;
    return cbDate && new Date(cbDate) < now;
  });

  const queueLeads = leads.filter((l) => l.manual_followup_required);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Students Needing Follow-up
          </h1>
          <p className="text-slate-500 mt-1">Students the AI couldn't reach — they need a personal call</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl"
          onClick={() => { setLoading(true); fetchQueue(); }}
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 text-pink-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-pink-600">{queueLeads.length}</p>
            <p className="text-xs text-slate-500">In Queue</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <CalendarClock className="w-5 h-5 text-blue-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-blue-600">{todaysCallbacks.length}</p>
            <p className="text-xs text-slate-500">Due Today</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-600">{overdueCallbacks.length}</p>
            <p className="text-xs text-slate-500">Overdue</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <PhoneCall className="w-5 h-5 text-green-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-600">{callbacks.length}</p>
            <p className="text-xs text-slate-500">Pending Callbacks</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full sm:w-auto sm:inline-grid">
          <TabsTrigger value="queue" className="text-xs sm:text-sm">Follow-up Queue</TabsTrigger>
          <TabsTrigger value="today" className="text-xs sm:text-sm">Today's Calls</TabsTrigger>
          <TabsTrigger value="callbacks" className="text-xs sm:text-sm">All Callbacks</TabsTrigger>
        </TabsList>

        {/* Follow-up Queue */}
        <TabsContent value="queue" className="mt-4">
          {queueLeads.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <PhoneCall className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">Queue is empty!</p>
                <p className="text-sm text-slate-400 mt-1">No students need follow-up right now</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {queueLeads.map((lead) => (
                <LeadQueueCard
                  key={lead.lead_id}
                  lead={lead}
                  onOpenDrawer={openDrawer}
                  onScheduleCallback={handleScheduleCallback}
                  inlineNote={inlineNotes[lead.lead_id] || ''}
                  onInlineNoteChange={(val) =>
                    setInlineNotes((prev) => ({ ...prev, [lead.lead_id]: val }))
                  }
                  onSaveNote={() => handleSaveInlineNote(lead.lead_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Today's Calls */}
        <TabsContent value="today" className="mt-4">
          {todaysCallbacks.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <CalendarClock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No calls scheduled for today</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {todaysCallbacks.map((cb) => (
                <CallbackCard
                  key={cb.callback_id}
                  callback={cb}
                  onOpenDrawer={openDrawer}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* All Callbacks */}
        <TabsContent value="callbacks" className="mt-4">
          {callbacks.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No pending callbacks</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {callbacks
                .sort((a, b) => new Date(a.scheduled_for || a.callback_date) - new Date(b.scheduled_for || b.callback_date))
                .map((cb) => (
                  <CallbackCard
                    key={cb.callback_id}
                    callback={cb}
                    onOpenDrawer={openDrawer}
                  />
                ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Lead Detail Drawer */}
      <LeadDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        leadId={selectedLeadId}
        onLeadUpdated={fetchQueue}
      />
    </div>
  );
};


const LeadQueueCard = ({ lead, onOpenDrawer, onScheduleCallback, inlineNote, onInlineNoteChange, onSaveNote }) => {
  const [noteExpanded, setNoteExpanded] = useState(false);
  const reason = lead.manual_followup_reason || lead.escalation_reason || 'escalation';
  const reasonLabel = REASON_LABELS[reason] || reason.replace(/_/g, ' ');

  // Calculate time in queue
  const escalatedAt = lead.escalated_at || lead.updated_at;
  let timeInQueue = '';
  if (escalatedAt) {
    const diff = Date.now() - new Date(escalatedAt).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) timeInQueue = 'Just now';
    else if (hours < 24) timeInQueue = `${hours}h in queue`;
    else timeInQueue = `${Math.floor(hours / 24)}d in queue`;
  }

  // AI attempt summary
  const aiCalls = lead.ai_call_count || 0;
  const aiSms = lead.ai_sms_count || 0;
  let aiSummary = '';
  if (aiCalls > 0 || aiSms > 0) {
    const parts = [];
    if (aiCalls > 0) parts.push(`${aiCalls} AI call${aiCalls > 1 ? 's' : ''}`);
    if (aiSms > 0) parts.push(`${aiSms} SMS`);
    aiSummary = parts.join(', ') + ' — no success';
  }

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        {/* Reason banner */}
        <div className="flex items-center gap-2 mb-3 px-3 py-1.5 bg-amber-50 rounded-lg">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
          <span className="text-xs font-medium text-amber-700">{reasonLabel}</span>
          {timeInQueue && (
            <span className="text-xs text-amber-500 ml-auto">{timeInQueue}</span>
          )}
        </div>

        <div className="flex items-start justify-between gap-3">
          {/* Lead info */}
          <div className="flex-1 min-w-0">
            <button
              className="text-sm font-semibold text-slate-900 hover:text-pink-600 transition-colors text-left"
              onClick={() => onOpenDrawer(lead.lead_id)}
            >
              {lead.first_name} {lead.last_name}
            </button>

            {aiSummary && (
              <p className="text-xs text-slate-400 mt-0.5">{aiSummary}</p>
            )}

            {lead.course_interest && (
              <Badge className="text-xs bg-pink-50 text-pink-600 mt-1.5">
                {lead.course_interest}
              </Badge>
            )}
          </div>

          {/* Call button — prominent */}
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              className="flex-shrink-0 w-11 h-11 rounded-xl bg-pink-500 hover:bg-pink-600 text-white flex items-center justify-center transition-colors shadow-sm"
            >
              <Phone className="w-5 h-5" />
            </a>
          )}
        </div>

        {/* Quick actions row */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              className="text-xs text-slate-500 hover:text-pink-600 transition-colors"
            >
              {lead.phone}
            </a>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            {/* Inline note toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
              onClick={() => setNoteExpanded(!noteExpanded)}
            >
              <StickyNote className="w-3.5 h-3.5" />
            </Button>

            {/* Schedule callback */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs rounded-lg border-slate-200"
                >
                  <CalendarClock className="w-3 h-3 mr-1" />
                  Schedule
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" align="end">
                <div className="space-y-1">
                  {CALLBACK_QUICK_OPTIONS.map((opt) => (
                    <button
                      key={opt.label}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-pink-50 text-slate-700 transition-colors"
                      onClick={() => onScheduleCallback(lead.lead_id, opt)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Inline note input */}
        {noteExpanded && (
          <div className="mt-3 flex gap-2">
            <Textarea
              value={inlineNote}
              onChange={(e) => onInlineNoteChange(e.target.value)}
              placeholder="Quick note..."
              className="rounded-lg text-xs min-h-[60px] flex-1 resize-none"
            />
            <Button
              size="sm"
              className="btn-primary rounded-lg h-auto self-end"
              disabled={!inlineNote?.trim()}
              onClick={onSaveNote}
            >
              Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const CallbackCard = ({ callback, onOpenDrawer }) => {
  const cbDate = callback.scheduled_for || callback.callback_date;
  const isOverdue = cbDate && new Date(cbDate) < new Date();
  const leadName = callback.lead_name || `${callback.first_name || ''} ${callback.last_name || ''}`.trim() || 'Unknown';

  return (
    <Card className={`border-0 shadow-sm ${isOverdue ? 'ring-1 ring-red-200' : ''}`}>
      <CardContent className="p-4 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <button
              className="text-sm font-medium text-slate-900 hover:text-pink-600 transition-colors"
              onClick={() => onOpenDrawer(callback.lead_id)}
            >
              {leadName}
            </button>
            {isOverdue && (
              <Badge className="text-xs bg-red-100 text-red-600">Overdue</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {cbDate && new Date(cbDate).toLocaleDateString('en-GB', {
              weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
            {callback.reason && ` — ${callback.reason}`}
          </p>
        </div>
        {callback.phone && (
          <a
            href={`tel:${callback.phone}`}
            className="flex-shrink-0 w-10 h-10 rounded-xl bg-pink-500 hover:bg-pink-600 text-white flex items-center justify-center transition-colors"
          >
            <Phone className="w-4 h-4" />
          </a>
        )}
      </CardContent>
    </Card>
  );
};

export default TrainingManualQueuePage;
